import type { components } from '@feedfm/clips-api-client';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import React, { useState } from 'react';
import { useDemoAppContext } from '../../pages/demo/DemoAppContext';
import './AuthForm.css';

const AuthForm = () => {
  const [token, setToken] = useState('primo-clips');
  const [secret, setSecret] = useState('primo-clips-secret');
  const [loading, setLoading] = useState(false);
  const [response, setResponse] = useState<
    components['schemas']['AuthenticateResponse'] | null
  >(null);
  const [error, setError] = useState<string | null>(null);
  const { client, setAuthToken } = useDemoAppContext();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setResponse(null);

    const response = await client.POST('/authenticate', {
      body: { token, secret },
    });

    setLoading(false);

    if (response.error) {
      setError(response.error ?? 'Failed to authenticate');
    }

    if (response.data) {
      setResponse(response.data);

      if (response.data.success) {
        setAuthToken(response.data.data.token);
      }
    }
  };

  return (
    <Paper elevation={3} className="auth-form-paper">
      <Typography variant="h5" component="h2" gutterBottom>
        Authenticate with Feed.fm
      </Typography>

      <form onSubmit={handleSubmit}>
        <Box className="auth-form-container">
          <TextField
            fullWidth
            label="Token"
            variant="outlined"
            value={token}
            onChange={(e) => {
              setToken(e.target.value);
            }}
            className="auth-form-textfield"
            required
            disabled
          />
          <TextField
            fullWidth
            label="Secret"
            variant="outlined"
            value={secret}
            onChange={(e) => {
              setSecret(e.target.value);
            }}
            className="auth-form-textfield"
            required
            disabled
          />
          <Button
            fullWidth
            variant="contained"
            type="submit"
            disabled={loading}
            className="auth-form-button"
          >
            {loading ? <CircularProgress size={24} /> : 'Authenticate'}
          </Button>
        </Box>
      </form>

      {error && (
        <Alert severity="error" className="auth-form-error">
          {error}
        </Alert>
      )}

      {response && (
        <Box className="auth-form-response">
          <Typography variant="h6" gutterBottom>
            Response:
          </Typography>
          <Paper variant="outlined" className="auth-form-response-paper">
            <pre className="auth-form-response-pre">
              {JSON.stringify(response, null, 2)}
            </pre>
          </Paper>
        </Box>
      )}

      {/* If we have an auth token and no response, it came from the cache. */}
      {client.getToken() && !response && (
        <Box className="auth-form-response">
          <Typography variant="h6" gutterBottom>
            Cached token:
          </Typography>
          <Paper variant="outlined" className="auth-form-response-paper">
            <pre className="auth-form-response-pre">{client.getToken()}</pre>
          </Paper>
        </Box>
      )}
    </Paper>
  );
};

export default AuthForm;
